import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Wrench,
  Search,
  Compass,
  ArrowRight,
  MapPin,
  Clock,
  X,
  CheckCircle2,
  Send,
} from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import { api, formatApiError, FLOORING_TYPES, floorLabel } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { kmToMiles } from "@/lib/units";
import Seo from "@/seo/Seo";

/**
 * Trade Wanted board.
 *
 * Public flow: fitters/tradespeople post material requests via the form.
 * Board shows anonymised previews (first name + initial, outward postcode
 * only, no contact details). Authenticated retailers can click "Reply" —
 * the reply routes through FloorMatch (Brevo) so the retailer never sees
 * the requester's raw email.
 *
 * Anti-circumvention mirrors listing behaviour: server-side PII scrubbing
 * on notes, IP + retailer rate-limits, and a 21-day auto-expiry on posts.
 */
export default function Trade() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [replyingTo, setReplyingTo] = useState(null); // trade request object

  async function reload() {
    setLoading(true);
    try {
      const params = filter ? `?flooring_type=${filter}` : "";
      const r = await api.get(`/trade/requests${params}`);
      setRequests(r.data.results || []);
    } catch {
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  return (
    <>
      <Seo
        title="Trade Wanted — post flooring requests, get matched with local surplus"
        description="Carpet fitters and flooring installers: post an exact material request (manufacturer, range, colour, dimensions) and let verified Greater Manchester retailers reply if they have matching surplus or offcuts."
        path="/trade"
        keywords="carpet fitter surplus request, flooring offcuts wanted, trade wanted Manchester, remnant matching, carpet fitter tools Manchester"
      />
      <PublicHeader />
      <main className="min-h-screen fm-grain" data-testid="trade-page">
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-10 sm:pt-14 pb-6">
          <div className="grid lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#2A5A3B] bg-[#2A5A3B]/10 px-3 py-1.5 rounded-full">
                <Wrench className="w-3.5 h-3.5" aria-hidden="true" /> FloorMatch for trade
              </span>
              <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-[1.05] mt-4">
                Short on flooring? <span className="text-[#E05915]">Ask the trade.</span>
              </h1>
              <p className="text-lg text-slate-600 max-w-xl mt-4">
                Post exactly what you need — manufacturer, range, colour, dimensions — and
                verified Greater Manchester retailers reply through FloorMatch if they've got
                matching surplus. Free to post, no membership.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                <Chip>Free to post</Chip>
                <Chip>Replies within 24 hours</Chip>
                <Chip>Your contact stays private</Chip>
              </div>
            </div>
            <div className="lg:col-span-5">
              <TradeRequestForm onCreated={reload} />
            </div>
          </div>
        </section>

        {/* Board */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24" data-testid="trade-board">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-[#E05915]">
                Open trade requests
              </p>
              <h2 className="font-display font-bold text-2xl sm:text-3xl text-slate-900">
                {loading
                  ? "Loading…"
                  : `${requests.length} live request${requests.length === 1 ? "" : "s"}`}
              </h2>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <label className="text-xs font-semibold text-slate-500">Filter</label>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="h-10 rounded-md border border-slate-300 px-2 focus:outline-none focus:ring-2 focus:ring-[#E05915]/30 focus:border-[#E05915]"
                data-testid="trade-filter"
              >
                <option value="">All types</option>
                {FLOORING_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {!loading && requests.length === 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-600">
              <Wrench className="w-6 h-6 text-slate-400 mx-auto mb-2" />
              No open trade requests right now. Be the first to post — retailers get an alert as
              soon as it goes live.
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            {requests.map((r) => (
              <TradeCard
                key={r.id}
                r={r}
                canReply={user?.role === "retailer"}
                onReply={() => setReplyingTo(r)}
              />
            ))}
          </div>

          {!user && requests.length > 0 && (
            <p className="text-sm text-slate-500 mt-6 text-center">
              Are you a retailer with matching stock?{" "}
              <Link to="/retailer/login" className="text-[#E05915] font-semibold hover:underline">
                Sign in to reply
              </Link>{" "}
              through FloorMatch — we mask requester contact details until they engage back.
            </p>
          )}
        </section>

        {replyingTo && (
          <ReplyDialog
            request={replyingTo}
            onClose={() => setReplyingTo(null)}
            onSent={() => {
              setReplyingTo(null);
              reload();
            }}
          />
        )}
      </main>
    </>
  );
}

function Chip({ children }) {
  return (
    <span className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-800 text-xs font-semibold px-3 py-1.5 rounded-full shadow-sm">
      <CheckCircle2 className="w-3.5 h-3.5 text-[#2A5A3B]" /> {children}
    </span>
  );
}

function TradeCard({ r, canReply, onReply }) {
  return (
    <div
      className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col"
      data-testid={`trade-card-${r.reference_code}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            {r.reference_code}
          </p>
          <h3 className="font-display font-semibold text-lg text-slate-900 mt-0.5 capitalize">
            {r.colour ? `${r.colour} ` : ""}
            {floorLabel(r.flooring_type)}
            {r.manufacturer ? ` · ${r.manufacturer}` : ""}
          </h3>
        </div>
        {r.needed_by && (
          <span className="inline-flex items-center gap-1 bg-[#E05915]/10 text-[#E05915] text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap">
            <Clock className="w-3 h-3" /> {r.needed_by}
          </span>
        )}
      </div>

      <ul className="mt-3 text-sm text-slate-700 space-y-1">
        {(r.min_width_m || r.min_length_m) && (
          <li>
            Minimum{" "}
            <b>
              {r.min_width_m || "?"}m × {r.min_length_m || "?"}m
            </b>
          </li>
        )}
        {r.coverage_m2 && (
          <li>
            Coverage needed: <b>{r.coverage_m2}m²</b>
          </li>
        )}
        {r.range_name && (
          <li>
            Range: <b>{r.range_name}</b>
          </li>
        )}
        {r.max_budget_gbp && (
          <li>
            Budget up to <b>£{r.max_budget_gbp}</b>
          </li>
        )}
        <li className="flex items-center gap-1 text-xs text-slate-500">
          <MapPin className="w-3.5 h-3.5" /> {r.postcode_area} area · within {Math.round(kmToMiles(r.radius_km))} miles · by{" "}
          {r.display_name}
        </li>
      </ul>

      {r.notes && (
        <p className="mt-3 text-sm text-slate-600 bg-slate-50 border border-slate-100 rounded-lg p-2.5 line-clamp-3">
          {r.notes}
        </p>
      )}

      <div className="mt-4 flex items-center justify-between gap-2">
        <span className="text-xs text-slate-400">{r.reply_count || 0} replies so far</span>
        {canReply ? (
          <button
            onClick={onReply}
            className="fm-btn-primary text-sm inline-flex items-center gap-1.5"
            data-testid={`trade-reply-${r.reference_code}`}
          >
            <Send className="w-3.5 h-3.5" /> Reply to {r.display_name}
          </button>
        ) : (
          <span className="text-xs text-slate-400 italic">Retailers only can reply</span>
        )}
      </div>
    </div>
  );
}

function TradeRequestForm({ onCreated }) {
  const [form, setForm] = useState({
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    flooring_type: "carpet",
    manufacturer: "",
    range_name: "",
    colour: "",
    min_width_m: "",
    min_length_m: "",
    coverage_m2: "",
    max_budget_gbp: "",
    postcode: "",
    radius_km: "25",
    needed_by: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ kind: "idle", text: "" });

  function up(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setMsg({ kind: "idle", text: "" });
    try {
      const body = {
        contact_name: form.contact_name,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone || undefined,
        flooring_type: form.flooring_type,
        manufacturer: form.manufacturer || undefined,
        range_name: form.range_name || undefined,
        colour: form.colour || undefined,
        min_width_m: form.min_width_m ? Number(form.min_width_m) : undefined,
        min_length_m: form.min_length_m ? Number(form.min_length_m) : undefined,
        coverage_m2: form.coverage_m2 ? Number(form.coverage_m2) : undefined,
        max_budget_gbp: form.max_budget_gbp ? Number(form.max_budget_gbp) : undefined,
        postcode: form.postcode,
        radius_km: Math.round(Number(form.radius_km || 25) * 1.60934),
        needed_by: form.needed_by || undefined,
        notes: form.notes || undefined,
      };
      await api.post("/trade/requests", body);
      setMsg({
        kind: "ok",
        text: "Your request is live. Retailers can now reply — we'll email you as soon as they do.",
      });
      setForm({
        contact_name: "",
        contact_email: "",
        contact_phone: "",
        flooring_type: "carpet",
        manufacturer: "",
        range_name: "",
        colour: "",
        min_width_m: "",
        min_length_m: "",
        coverage_m2: "",
        max_budget_gbp: "",
        postcode: "",
        radius_km: "25",
        needed_by: "",
        notes: "",
      });
      onCreated?.();
    } catch (err) {
      setMsg({ kind: "err", text: formatApiError(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={submit}
      className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 shadow-sm"
      data-testid="trade-form"
    >
      <p className="text-xs font-semibold uppercase tracking-wider text-[#E05915]">
        Post a trade request
      </p>
      <h2 className="font-display font-bold text-xl text-slate-900 mt-1">
        What flooring do you need?
      </h2>

      <div className="grid grid-cols-2 gap-3 mt-4">
        <Field label="Your name">
          <input
            required
            className="fm-input"
            value={form.contact_name}
            onChange={(e) => up("contact_name", e.target.value)}
            data-testid="trade-name"
          />
        </Field>
        <Field label="Email">
          <input
            type="email"
            required
            className="fm-input"
            value={form.contact_email}
            onChange={(e) => up("contact_email", e.target.value)}
            data-testid="trade-email"
          />
        </Field>
        <Field label="Flooring type" full>
          <select
            className="fm-input"
            value={form.flooring_type}
            onChange={(e) => up("flooring_type", e.target.value)}
            data-testid="trade-type"
          >
            {FLOORING_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Manufacturer">
          <input
            className="fm-input"
            placeholder="e.g. Cormar"
            value={form.manufacturer}
            onChange={(e) => up("manufacturer", e.target.value)}
          />
        </Field>
        <Field label="Range / Colour">
          <input
            className="fm-input"
            placeholder="e.g. Primo Plus, Pewter"
            value={form.colour}
            onChange={(e) => up("colour", e.target.value)}
          />
        </Field>
        <Field label="Min width (m)">
          <input
            type="number"
            step="0.1"
            className="fm-input"
            value={form.min_width_m}
            onChange={(e) => up("min_width_m", e.target.value)}
          />
        </Field>
        <Field label="Min length (m)">
          <input
            type="number"
            step="0.1"
            className="fm-input"
            value={form.min_length_m}
            onChange={(e) => up("min_length_m", e.target.value)}
          />
        </Field>
        <Field label="Your postcode">
          <input
            required
            className="fm-input"
            placeholder="M4 1HW"
            value={form.postcode}
            onChange={(e) => up("postcode", e.target.value.toUpperCase())}
            data-testid="trade-postcode"
          />
        </Field>
        <Field label="Within (miles)">
          <select
            className="fm-input"
            value={form.radius_km}
            onChange={(e) => up("radius_km", e.target.value)}
          >
            <option value="5">5 miles</option>
            <option value="10">10 miles</option>
            <option value="25">25 miles</option>
            <option value="50">50 miles</option>
          </select>
        </Field>
        <Field label="Needed by" full>
          <select
            className="fm-input"
            value={form.needed_by}
            onChange={(e) => up("needed_by", e.target.value)}
          >
            <option value="">Whenever</option>
            <option value="today">Today</option>
            <option value="this week">This week</option>
            <option value="next week">Next week</option>
          </select>
        </Field>
        <Field label="Anything else? (optional)" full>
          <textarea
            className="w-full rounded-md border border-slate-300 px-3 py-2 min-h-[80px] focus:outline-none focus:ring-2 focus:ring-[#E05915]/30 focus:border-[#E05915]"
            placeholder="e.g. underlay needed too, must be same batch as attached room…"
            value={form.notes}
            onChange={(e) => up("notes", e.target.value)}
            data-testid="trade-notes"
          />
        </Field>
      </div>

      {msg.kind === "err" && (
        <div className="mt-3 text-sm text-red-600" data-testid="trade-form-error">
          {msg.text}
        </div>
      )}
      {msg.kind === "ok" && (
        <div className="mt-3 bg-[#2A5A3B]/10 border border-[#2A5A3B]/20 text-[#2A5A3B] text-sm rounded-lg p-3 flex items-start gap-2" data-testid="trade-form-success">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          {msg.text}
        </div>
      )}

      <button
        type="submit"
        disabled={busy}
        className="fm-btn-primary w-full mt-4 disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid="trade-submit"
      >
        {busy ? "Posting…" : "Post request"}
      </button>
      <p className="text-[11px] text-slate-500 mt-3">
        Your email and phone stay private — retailers can only contact you through FloorMatch.
      </p>
    </form>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={full ? "col-span-2" : ""}>
      <span className="fm-label">{label}</span>
      {children}
    </label>
  );
}

function ReplyDialog({ request, onClose, onSent }) {
  const [message, setMessage] = useState("");
  const [listingId, setListingId] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function submit(e) {
    e.preventDefault();
    if (!message || message.length < 10) {
      setErr("Message must be at least 10 characters.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await api.post(`/trade/requests/${request.id}/reply`, {
        message,
        listing_id: listingId || undefined,
      });
      onSent();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="trade-reply-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-[#E05915]">
              Reply to {request.reference_code}
            </p>
            <h2 className="font-display font-bold text-xl text-slate-900 capitalize">
              {request.colour ? `${request.colour} ` : ""}
              {floorLabel(request.flooring_type)}
            </h2>
          </div>
          <button aria-label="Close" onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <span className="fm-label">Message to {request.display_name}</span>
            <textarea
              className="w-full rounded-md border border-slate-300 px-3 py-2 min-h-[120px] focus:outline-none focus:ring-2 focus:ring-[#E05915]/30 focus:border-[#E05915]"
              placeholder="Yes — I have a 4.2m × 2m Cormar Pewter roll end in stock, £145. Available for collection this week."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="trade-reply-message"
              required
            />
          </label>
          <label className="block">
            <span className="fm-label">Link a listing (optional)</span>
            <input
              className="fm-input"
              placeholder="Listing ID or reference code"
              value={listingId}
              onChange={(e) => setListingId(e.target.value)}
            />
          </label>
          {err && <div className="text-sm text-red-600" data-testid="trade-reply-error">{err}</div>}
          <button
            type="submit"
            disabled={busy}
            className="fm-btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="trade-reply-submit"
          >
            {busy ? "Sending…" : "Send reply through FloorMatch"}
          </button>
          <p className="text-[11px] text-slate-500 text-center">
            We route this via email — your shop's name and this message are sent, requester's email
            stays private.
          </p>
        </form>
      </div>
    </div>
  );
}
